import { fg } from '@atlaskit/platform-feature-flags';

import { parseExtensionId } from '../../utils';
import { emitFrontendCustomMetric as emitFrontendCustomMetricQuery } from '../../web-client/queries/emitFrontendCustomMetric';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';

// ─── emitFrontendCustomMetric ─────────────────────────────────────────────────

type EmitFrontendCustomMetricPayload = {
	customMetricName: string;
	value: number;
};

type EmitFrontendCustomMetricContext = MakeBridgeContext<
	'extension' | 'extensionDetails' | 'contextIds',
	'relayEnvironment'
>;

/**
 * Emits a Forge frontend custom metric from a Custom UI iframe.
 *
 * Enriches the raw `{ customMetricName, value }` payload with host-side app metadata
 * (appId, installationId, contextAri, moduleKey) and forwards it through AGG. Gated on
 * `platform_forge_frontend_custom_metrics`; fire-and-forget and silent-fail so emitting a
 * metric can never surface an error to the app.
 *
 * This is the v2-bridge equivalent of the `emitFrontendCustomMetric` handler in
 * `custom-ui/bridge/useBridge.tsx` + `InnerIframe.tsx`, so both bridge runtimes behave
 * identically. `moduleKey` is the module *key* parsed from the extension ARI (not
 * `extension.type`, which is the module type).
 */
export const emitFrontendCustomMetric: TBridgeMethod<
	EmitFrontendCustomMetricContext,
	EmitFrontendCustomMetricPayload,
	void
> = async (
	{ extension, extensionDetails, contextIds, services: { relayEnvironment } },
	payload,
) => {
	if (!fg('platform_forge_frontend_custom_metrics')) {
		return;
	}

	// No context ARI → bail. `contextIds[0]` is typed `string` but is `undefined` for an empty array.
	const contextAri = contextIds[0];
	if (!contextAri) {
		return;
	}

	try {
		const { moduleKey } = parseExtensionId(extension.id);
		await emitFrontendCustomMetricQuery({
			relayEnvironment,
			contextAri,
			appId: extensionDetails.appId,
			installationId: extension.installationId,
			moduleKey,
			customMetricName: payload.customMetricName,
			value: payload.value,
		});
	} catch {
		// Silent fail: emitting a metric must never surface an error to the app.
	}
};
